import { ScrollView, View } from "react-native-web";
import { DEPRECATED_useModulePublicData, useDatastore, useServerCallResult } from "util/datastore";
import { useState } from "react";
import { Heading, UtilityText } from "component/text";
import { HorizBox, Pad, PadBox } from "component/basics";
import { BreadCrumb, CTAButton, IconButton } from "component/button";
import { colorBorderPrimary, colorButtonAccent, colorTextHighlight, colorTextHint, colorTextPrimary, colorTextWarning } from "component/color";
import { Add, Edit, TrashCan } from "@carbon/icons-react";
import { gotoInstanceScreen, pushSubscreen } from "util/navigate";
import { useHasCapability, RestrictedScreen } from "component/admin";
import { StyleSheet } from "react-native";
import { ChannelEditorScreen, ChannelImage, DefaultChannelTooltip } from "open-modules/zdf/component/channels/channeleditor";
import { REPLACE_ZDF_DropDownSelector } from 'open-modules/zdf/component/channels/channel-dropdown';
import { isValidEmail } from "open-modules/zdf/util/channel-util";

export const AdminManageChannelsFeature = {
    name: "Manage Channels",
    key: "adminmanagechannels",
    parent: "channelbase",
    subscreens: {
        manageChannels: ManageChannelsScreen,
        createNewChannel: ChannelEditorScreen
    },
    config: {
        quickLinks: [{ label: "Channels", screenKey: "manageChannels" }],
    },
};

const ManageChannelsScreenStyle = StyleSheet.create({
    channelViewer: {
        display: "flex",
        flexDirection: "column",
        gap: 4,
    },
});

export function ManageChannelsScreen() {
    const s = ManageChannelsScreenStyle;

    const canViewChannels = useHasCapability("channel/view");
    const channels = DEPRECATED_useModulePublicData("channel", ["all"]);
    const channelsArray = channels ? Object.values(channels) : [];
    const [currentChannel, setCurrentChannel] = useState(null);
    const displayedChannels = Object.values(channels ?? {}).sort();
    const currentDisplayedChannel = channels?.[currentChannel?.key];
    const hasDefaultChannel = channelsArray.length > 0 ? !!channelsArray.find((channel) => channel.isDefault) : false;

    function onCurrentChannelSelected(selectedChannel) {
        setCurrentChannel(selectedChannel);
    }

    function onNewChannel() {
        pushSubscreen("createNewChannel");
    }

    return (
        <RestrictedScreen capability="channel/view">
            <PadBox horiz={20} vert={20}>
                <View style={s.channelViewer}>
                    <Heading label={"Manage Channels"} type="large" />
                    {!hasDefaultChannel && <NoDefaultChannelHint />}
                    <HorizontalChannelView
                        channels={displayedChannels}
                        selectedChannel={currentDisplayedChannel}
                        setCurrentChannel={onCurrentChannelSelected}
                        onNewChannel={onNewChannel}
                    />
                    {currentDisplayedChannel && <ChannelSettingsMenu selectedChannel={currentDisplayedChannel} />}
                </View>
            </PadBox>
        </RestrictedScreen>
    );
}

function NoDefaultChannelHint() {
    return (
        <PadBox vert={8}>
            <HorizBox>
                <UtilityText label="You don't have a default channel yet!" />
                <Pad size={8} />
                <DefaultChannelTooltip />
            </HorizBox>
        </PadBox>
    );
}

export function HorizontalChannelView({ channels, selectedChannel, setCurrentChannel, onNewChannel }) {
    const canCreateChannel = useHasCapability("channel/create");
    return (
        <ScrollView
            horizontal={true}
            scrollEnabled
            style={{ width: "100%" }}
            contentContainerStyle={{ alignItems: "flex-start" }}
        >
            {channels.map((channelData) => (
                <SelectableChannelTile
                    onSelected={setCurrentChannel}
                    isSelected={selectedChannel?.key === channelData.key}
                    key={channelData.key}
                    channel={channelData}
                />
            ))}
            {onNewChannel && canCreateChannel && <NewChannelTile onPress={onNewChannel} />}
        </ScrollView>
    );
}

const SelectableChannelTileStyle = StyleSheet.create({
    channelTile: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        margin: 8,
        flexShrink: 1,
        cursor: "pointer",
    },
});

function SelectableChannelTile({ channel, onSelected, isSelected }) {
    const s = SelectableChannelTileStyle;

    function onClick() {
        onSelected(channel);
    }

    return (
        <View onClick={onClick} style={s.channelTile} testID={`selectable-tile-`+channel.key}>
            <ChannelImage showBorder={isSelected} imageUrl={channel.photoUrl} />
            <Pad size={8} />
            <UtilityText weight={"medium"} text={channel.name} color={isSelected ? colorTextHighlight : colorTextPrimary} />
            <Pad size={4} />
            {channel.isDefault && <UtilityText label="default" color={colorTextHint} />}
        </View>
    );
}

const NewChannelTileStyle = StyleSheet.create({
    channelTile: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        margin: 8,
        flexShrink: 1,
        cursor: "pointer",
    },
});

function NewChannelTile({ onPress }) {
    const s = NewChannelTileStyle;

    return (
        <View onClick={onPress} style={s.channelTile} testID="new-channel-tile">
            <ChannelImage icon={Add} />
            <Pad size={8} />
            <UtilityText weight={"medium"} label={"Create a new channel"} />
        </View>
    );
}

function ChannelSettingsMenu({ selectedChannel }) {
    const [refreshCounter, setRefreshCounter] = useState(0);

    const canManageMembers = useHasCapability("channel/manage-members");
    const canEditChannel = useHasCapability("channel/edit");

    const channelMembers = useServerCallResult("channel", "getChannelMembers", {
        channelKey: selectedChannel?.key,
        refreshCounter,
    });
    const channelMemberArray = Object.values(channelMembers?.["memberEmails"] || {});

    if (!selectedChannel || !selectedChannel.key) return null;

    return (
        <View>
            {canEditChannel && <EditChannelButton channel={selectedChannel} />}
            <Pad size={24} />
            <Heading label={"Channel Members"} />
            <Pad size={8} />
            {canManageMembers && (
                <ChannelMemberManagementMenu
                    channel={selectedChannel}
                    channelMemberArray={channelMemberArray}
                    setRefreshCounter={setRefreshCounter}
                />
            )}
            <ChannelMemberList
                channel={selectedChannel}
                channelMemberArray={channelMemberArray}
                setRefreshCounter={setRefreshCounter}
            />
            {channelMemberArray.length > 0 && <Pad size={16} />}
            {selectedChannel.isDefault ? (
                <UtilityText label="All admin users have access to this channel." />
            ) : (
                <UtilityText label="All Super-Moderators have access to this channel." />
            )}
        </View>
    );
}

function EditChannelButton({channel}) {
    return (
        <IconButton
            label="Edit Channel"
            ariaLabel={"Edit Channel"}
            icon={Edit}
            iconProps={{ color: colorButtonAccent }}
            onPress={() =>
                gotoInstanceScreen({
                    structureKey: "channel",
                    instanceKey: channel.key,
                    screenKey: "edit",
                })
            }
        />
    );
}

const ChannelMemberManagementMenuStyle = StyleSheet.create({
    addMemberMenu: {
        display: "flex",
        flexDirection: "row",
        gap: 4,
        alignItems: "center",
        justifyContent: "space-between"
    },
});

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
function ChannelMemberManagementMenu({ channel, channelMemberArray, setRefreshCounter }) {
    const s = ChannelMemberManagementMenuStyle;

    const datastore = useDatastore();
    const [newMemberEmail, setNewMemberEmail] = useState(null);
    const adminUsers = useServerCallResult("admin", "getAdminUsers", {});

    const initalOptions = [{ key: "Select a user to add", label: "No user selected" }];
    const options = initalOptions
        .concat(
            (adminUsers ?? []).map((user) => {
                return { key: user.email, text: user.email };
            })
        )
        .filter((user) => !channelMemberArray.includes(user.key));

    async function addToMembershipList(email) {
        if (email !== "No user selected") {
            setNewMemberEmail(email);
            setRefreshCounter((v) => v + 1);
        }
    }

    async function addMemberByEmailAsync({ email }) {
        if (isValidEmail({ email })) {
            await datastore.callServerAsync("channel", "addMemberByEmail", {
                email: newMemberEmail,
                channelKey: channel.key,
            });
            setRefreshCounter((c) => c + 1);
        }
    }

    return (
        <View style={s.addMemberMenu}>
            <HorizBox gap={8}>
                <UtilityText label="Select a user to add:" />
                <View>
                    <REPLACE_ZDF_DropDownSelector
                        testID={"add-member-dropdown"}
                        options={options.length > 1 ? options : [{ key: "No admin users", label: "No admin users" }]}
                        value={newMemberEmail ?? "No admin users"}
                        onChange={(v) => addToMembershipList(v)}
                    />
                </View>
            </HorizBox>
            <CTAButton label={"Add Moderator"} onPress={() => addMemberByEmailAsync({ email: newMemberEmail })} />
        </View>
    );
}

const ChannelMemberListStyle = StyleSheet.create({
    scrollableMemberList: {
        marginTop: 8,
        flexFlow: 1,
        width: "100%",
    },
});

function ChannelMemberList({ channel, channelMemberArray, setRefreshCounter }) {
    const s = ChannelMemberListStyle;
    
    return (
        <ScrollView style={s.scrollableMemberList}>
            {channelMemberArray.map((email, index) => (
                <View key={email}>
                    {index > 0 && <Pad size={5} />}
                    <MemberTile
                        email={email}
                        channel={channel}
                        onRemove={() => setRefreshCounter((c) => c + 1)}
                    />
                </View>
            ))}
        </ScrollView>
    );
}

const MemberTileStyle = StyleSheet.create({
    memberTile: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        border: "1px solid " + colorBorderPrimary,
        padding: 8,
        flexGrow: 1,
        width: "100%",
        borderRadius: "8px"
    }
});

function MemberTile({ email, channel, onRemove }) {
    const s = MemberTileStyle;
    const datastore = useDatastore();
    const canManageMembers = useHasCapability("channel/manage-members");

    async function removeMemberFromChannelAsync() {
        await datastore.callServerAsync("channel", "removeMemberFromChannel", { email, channelKey: channel.key });
        onRemove();
    }

    return (
        <View style={s.memberTile}>
            <UtilityText text={email} weight={"medium"} />
            {canManageMembers && (
                <BreadCrumb
                    testID={`delete-member-${email}`}
                    icon={TrashCan}
                    iconProps={{ color: colorTextWarning, size: 24 }}
                    onPress={() => removeMemberFromChannelAsync()}
                />
            )}
        </View>
    );
}